import type { ReactNode } from 'react';
import {
  AlignCenterOutlined,
  AlignLeftOutlined,
  AlignRightOutlined,
  BoldOutlined,
  ClearOutlined,
  ItalicOutlined,
  MenuOutlined,
  OrderedListOutlined,
  RedoOutlined,
  UnderlineOutlined,
  UndoOutlined,
  UnorderedListOutlined,
} from '@ant-design/icons';

import { useT } from '../../../i18n';
import type { KhoaDich } from '../../../i18n';
import { cn } from '../../../lib/cn';

const CO_CHU: Array<{ value: string; khoa: KhoaDich }> = [
  { value: '2', khoa: 'vanBanCc.co.2' },
  { value: '3', khoa: 'vanBanCc.co.3' },
  { value: '4', khoa: 'vanBanCc.co.4' },
  { value: '5', khoa: 'vanBanCc.co.5' },
  { value: '6', khoa: 'vanBanCc.co.6' },
];

function Nut({ nhan, onExec, children }: { nhan: string; onExec: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      title={nhan}
      aria-label={nhan}
      // Giữ vùng chọn trong vùng soạn thảo: bấm nút không được lấy mất focus
      onMouseDown={(e) => e.preventDefault()}
      onClick={onExec}
      className="inline-flex h-7 min-w-7 items-center justify-center rounded border border-transparent px-1.5 text-content-secondary hover:border-default hover:bg-subtle focus-visible:bg-subtle"
    >
      {children}
    </button>
  );
}

const Vach = () => <span aria-hidden className="mx-1 h-4 w-px bg-strong" />;

/**
 * Thanh định dạng cho vùng `contentEditable` đang focus (`document.execCommand`), chuyển từ
 * `RichTextToolbar` của foxainative. `onAfterExec` đánh dấu "đã chỉnh tay".
 */
export function ThanhCongCuVanBan({ onAfterExec, className }: { onAfterExec?: () => void; className?: string }) {
  const { t } = useT();
  const exec = (lenh: string, giaTri?: string) => {
    try {
      document.execCommand(lenh, false, giaTri);
    } catch {
      // Trình duyệt không còn execCommand: bỏ qua, người dùng vẫn gõ được
    }
    onAfterExec?.();
  };

  return (
    <div
      role="toolbar"
      aria-label={t('vanBanCc.thanhCongCu')}
      className={cn('flex flex-wrap items-center gap-0.5 border-b border-default bg-page px-2 py-1.5', className)}
    >
      <Nut nhan={t('vanBanCc.dam')} onExec={() => exec('bold')}>
        <BoldOutlined />
      </Nut>
      <Nut nhan={t('vanBanCc.nghieng')} onExec={() => exec('italic')}>
        <ItalicOutlined />
      </Nut>
      <Nut nhan={t('vanBanCc.gachChan')} onExec={() => exec('underline')}>
        <UnderlineOutlined />
      </Nut>
      <Vach />
      <Nut nhan={t('vanBanCc.canTrai')} onExec={() => exec('justifyLeft')}>
        <AlignLeftOutlined />
      </Nut>
      <Nut nhan={t('vanBanCc.canGiua')} onExec={() => exec('justifyCenter')}>
        <AlignCenterOutlined />
      </Nut>
      <Nut nhan={t('vanBanCc.canPhai')} onExec={() => exec('justifyRight')}>
        <AlignRightOutlined />
      </Nut>
      <Nut nhan={t('vanBanCc.canDeu')} onExec={() => exec('justifyFull')}>
        <MenuOutlined />
      </Nut>
      <Vach />
      <Nut nhan={t('vanBanCc.dsCham')} onExec={() => exec('insertUnorderedList')}>
        <UnorderedListOutlined />
      </Nut>
      <Nut nhan={t('vanBanCc.dsSo')} onExec={() => exec('insertOrderedList')}>
        <OrderedListOutlined />
      </Nut>
      <Vach />
      <select
        title={t('vanBanCc.coChu')}
        aria-label={t('vanBanCc.coChu')}
        onMouseDown={(e) => e.stopPropagation()}
        onChange={(e) => {
          exec('fontSize', e.target.value);
          e.target.selectedIndex = 0;
        }}
        className="h-7 rounded border border-default bg-surface px-1 text-xs text-content-secondary"
      >
        <option>{t('vanBanCc.coChu')}</option>
        {CO_CHU.map((c) => (
          <option key={c.value} value={c.value}>
            {t(c.khoa)}
          </option>
        ))}
      </select>
      <Vach />
      <Nut nhan={t('vanBanCc.hoanTac')} onExec={() => exec('undo')}>
        <UndoOutlined />
      </Nut>
      <Nut nhan={t('vanBanCc.lamLai')} onExec={() => exec('redo')}>
        <RedoOutlined />
      </Nut>
      <Nut nhan={t('vanBanCc.xoaDinhDang')} onExec={() => exec('removeFormat')}>
        <ClearOutlined />
      </Nut>
    </div>
  );
}
